import type {
  Card,
  CardRarity,
  Sale,
  SortingBin,
  SortingCategory,
} from "@/context/AppContext";

export type LotTier = "Bulk" | "Holo" | "Rare" | "Hits";

export interface LotMix {
  bulk: number;
  holo: number;
  rare: number;
  hits: number;
}

export interface LotAnalyzerInput {
  price: number;
  cardCount: number;
  mix: LotMix;
  cards: Card[];
  sales: Sale[];
  targetRoi?: number;
  feeRate?: number;
}

export interface TierBreakdown {
  tier: LotTier;
  cards: number;
  avgPrice: number;
  /** Lower bound of the per-card price estimate (mean - stddev or fallback). */
  lowPrice: number;
  /** Upper bound of the per-card price estimate. */
  highPrice: number;
  sampleSize: number;
  revenue: number;
  /** True when sampleSize is below the confidence threshold (10). */
  lowConfidence: boolean;
  /** True when sampleSize === 0 — fully synthetic from defaults. */
  noHistory: boolean;
  /** How this tier groups for resale (e.g. 100-card bulk lots, individual hits). */
  bundleType: string;
  /** Estimated number of distinct listings/bundles produced from this tier. */
  bundleCount: number;
}

export interface LotAnalysisResult {
  estimatedRevenue: { min: number; expected: number; max: number };
  estimatedProfit: { min: number; expected: number; max: number };
  /** ROI % at the expected scenario, computed against the input price. */
  roi: number;
  /** Maximum price at which expected ROI hits targetRoi (default 50%). */
  suggestedMaxBid: number;
  /** Risk band derived from total sample size + revenue spread. */
  risk: "Low" | "Medium" | "High";
  /** Sum of historical sales across all tiers. */
  sampleSize: number;
  breakdown: TierBreakdown[];
  /** True when at least one tier has insufficient history for reliable pricing. */
  hasLowConfidenceTiers: boolean;
}

const TIERS: readonly LotTier[] = ["Bulk", "Holo", "Rare", "Hits"];

const DEFAULT_PRICES: Record<LotTier, number> = {
  Bulk: 0.05,
  Holo: 0.5,
  Rare: 0.75,
  Hits: 8.0,
};

// Confidence threshold per the task spec ("e.g. < 10 sales in a tier").
export const TIER_CONFIDENT_SAMPLE = 10;

// How tier cards are typically resold — drives the cards → bundles → revenue breakdown.
const TIER_BUNDLE_INFO: Record<
  LotTier,
  { type: string; cardsPerBundle: number }
> = {
  Bulk: { type: "100-card bulk lots", cardsPerBundle: 100 },
  Holo: { type: "25-card holo lots", cardsPerBundle: 25 },
  Rare: { type: "Individual / 5-card rare lots", cardsPerBundle: 5 },
  Hits: { type: "Individual hit singles", cardsPerBundle: 1 },
};

// Maps a Card's (rarity, finish) onto our analyzer tier buckets. Finish is
// checked first because the two-axis refactor moved Holo / Reverse Holo /
// Premium out of the rarity enum and onto Card.finish.
export function rarityToTier(
  rarity: CardRarity,
  finish?: import("@/context/AppContext").CardFinish,
): LotTier | null {
  if (finish === "Premium") return "Hits";
  if (finish === "Holo" || finish === "Reverse Holo") return "Holo";
  switch (rarity) {
    case "Common":
    case "Uncommon":
    case "Trainer":
    case "Energy":
      return "Bulk";
    case "Promo":
      return "Holo";
    case "Rare":
      return "Rare";
    case "Damaged":
    case "Sealed":
    default:
      return null;
  }
}

export interface TierStat {
  prices: number[];
  mean: number;
  stdDev: number; // sample std dev; 0 when <2 samples
}

function aggregateTierStats(
  cards: Card[],
  sales: Sale[],
): Record<LotTier, TierStat> {
  const cardsById = new Map<string, Card>();
  for (const c of cards) cardsById.set(c.id, c);

  // Sale has no per-sale quantity field, and addSale/completeSale do not adjust
  // Card.quantity, so a card record with quantity > 1 makes the meaning of
  // salePrice ambiguous (could be one card or the whole stack). To keep the
  // per-card mean honest we only treat singletons (quantity <= 1) as reliable
  // per-card observations and skip multi-quantity records entirely.
  const buckets: Record<LotTier, number[]> = {
    Bulk: [],
    Holo: [],
    Rare: [],
    Hits: [],
  };
  for (const sale of sales) {
    if (sale.itemType !== "Card") continue;
    const card = cardsById.get(sale.itemId);
    if (!card) continue;
    if ((card.quantity ?? 1) > 1) continue;
    const tier = rarityToTier(card.rarity, card.finish);
    if (!tier) continue;
    if (!Number.isFinite(sale.salePrice) || sale.salePrice <= 0) continue;
    buckets[tier].push(sale.salePrice);
  }

  const out = {} as Record<LotTier, TierStat>;
  for (const tier of TIERS) {
    const prices = buckets[tier];
    if (prices.length === 0) {
      out[tier] = { prices, mean: 0, stdDev: 0 };
      continue;
    }
    const mean = prices.reduce((a, b) => a + b, 0) / prices.length;
    let stdDev = 0;
    if (prices.length >= 2) {
      const variance =
        prices.reduce((s, v) => s + (v - mean) ** 2, 0) / (prices.length - 1);
      stdDev = Math.sqrt(variance);
    }
    out[tier] = { prices, mean, stdDev };
  }
  return out;
}

export function tierBounds(
  stat: TierStat,
  fallback: number,
): { low: number; expected: number; high: number } {
  const n = stat.prices.length;
  if (n === 0) {
    // No data — wide ±50% band around the generic default.
    return { low: fallback * 0.5, expected: fallback, high: fallback * 1.5 };
  }
  if (n < TIER_CONFIDENT_SAMPLE) {
    // Too few samples to trust the user's mean — blend toward the default in proportion to n,
    // and widen the band. weight grows from 1/10 (n=1) to 9/10 (n=9).
    const w = n / TIER_CONFIDENT_SAMPLE;
    const blended = stat.mean * w + fallback * (1 - w);
    // Band uses the larger of (stddev, 35% of expected) so outputs never look falsely precise.
    const halfBand = Math.max(stat.stdDev || 0, blended * 0.35);
    return {
      low: Math.max(0.01, blended - halfBand),
      expected: blended,
      high: blended + halfBand,
    };
  }
  // n >= TIER_CONFIDENT_SAMPLE — trust the user's mean and one sample stddev.
  const lo = Math.max(0.01, stat.mean - stat.stdDev);
  const hi = stat.mean + stat.stdDev;
  return { low: lo, expected: stat.mean, high: Math.max(hi, stat.mean) };
}

/**
 * Allocate integer card counts per tier from a percentage mix without drift —
 * largest-remainder method ensures the per-tier counts sum exactly to total.
 */
export function allocateCards(
  total: number,
  mixPct: Record<LotTier, number>,
): Record<LotTier, number> {
  const raw: Record<LotTier, number> = {} as Record<LotTier, number>;
  const floors: Record<LotTier, number> = {} as Record<LotTier, number>;
  let assigned = 0;
  for (const t of TIERS) {
    raw[t] = (total * mixPct[t]) / 100;
    floors[t] = Math.floor(raw[t]);
    assigned += floors[t];
  }
  let remainder = total - assigned;
  if (remainder > 0) {
    const order = [...TIERS].sort(
      (a, b) => raw[b] - floors[b] - (raw[a] - floors[a]),
    );
    for (const t of order) {
      if (remainder <= 0) break;
      floors[t] += 1;
      remainder -= 1;
    }
  }
  return floors;
}

function clampMixPercent(p: number): number {
  if (!Number.isFinite(p)) return 0;
  return Math.max(0, Math.min(100, p));
}

export function analyzeLot(input: LotAnalyzerInput): LotAnalysisResult {
  const targetRoi = input.targetRoi ?? 0.5;
  const feeRate = input.feeRate ?? 0.13;
  const cardCount = Math.max(0, Math.floor(input.cardCount || 0));
  const price = Math.max(0, input.price || 0);

  const mixPct = {
    Bulk: clampMixPercent(input.mix.bulk),
    Holo: clampMixPercent(input.mix.holo),
    Rare: clampMixPercent(input.mix.rare),
    Hits: clampMixPercent(input.mix.hits),
  };

  const stats = aggregateTierStats(input.cards, input.sales);

  let revLow = 0;
  let revExp = 0;
  let revHigh = 0;
  let totalSamples = 0;
  let hasLowConfidenceTiers = false;

  const cardAllocation = allocateCards(cardCount, mixPct);

  const breakdown: TierBreakdown[] = TIERS.map((tier) => {
    const tierCards = cardAllocation[tier];
    const bounds = tierBounds(stats[tier], DEFAULT_PRICES[tier]);
    const sampleSize = stats[tier].prices.length;
    const lowConfidence = sampleSize < TIER_CONFIDENT_SAMPLE;
    const noHistory = sampleSize === 0;

    const tierRevExp = tierCards * bounds.expected;
    const tierRevLow = tierCards * bounds.low;
    const tierRevHigh = tierCards * bounds.high;

    revLow += tierRevLow;
    revExp += tierRevExp;
    revHigh += tierRevHigh;
    totalSamples += sampleSize;
    if (lowConfidence && tierCards > 0) hasLowConfidenceTiers = true;

    const bundleInfo = TIER_BUNDLE_INFO[tier];
    const bundleCount =
      tierCards > 0
        ? Math.max(1, Math.ceil(tierCards / bundleInfo.cardsPerBundle))
        : 0;

    return {
      tier,
      cards: tierCards,
      avgPrice: bounds.expected,
      lowPrice: bounds.low,
      highPrice: bounds.high,
      sampleSize,
      revenue: tierRevExp,
      lowConfidence,
      noHistory,
      bundleType: bundleInfo.type,
      bundleCount,
    };
  });

  const netMultiplier = 1 - feeRate;
  const profitLow = revLow * netMultiplier - price;
  const profitExp = revExp * netMultiplier - price;
  const profitHigh = revHigh * netMultiplier - price;

  const roi = price > 0 ? (profitExp / price) * 100 : 0;

  // suggestedMaxBid: solve expectedNetRevenue / suggested - 1 = targetRoi
  // => suggested = expectedNetRevenue / (1 + targetRoi)
  const expectedNet = revExp * netMultiplier;
  const suggestedMaxBid = targetRoi > -1 ? expectedNet / (1 + targetRoi) : 0;

  const spreadRatio = revExp > 0 ? (revHigh - revLow) / revExp : Infinity;
  let risk: "Low" | "Medium" | "High";
  if (totalSamples < TIER_CONFIDENT_SAMPLE || hasLowConfidenceTiers) {
    risk = "High";
  } else if (totalSamples < 30 || spreadRatio > 0.5) {
    risk = "Medium";
  } else {
    risk = "Low";
  }

  return {
    estimatedRevenue: { min: revLow, expected: revExp, max: revHigh },
    estimatedProfit: { min: profitLow, expected: profitExp, max: profitHigh },
    roi,
    suggestedMaxBid,
    risk,
    sampleSize: totalSamples,
    breakdown,
    hasLowConfidenceTiers,
  };
}

// Maps every SortingCategory onto an analyzer tier so the derived card count
// and mix remain mutually consistent (no silent drops, no normalization
// inflating supported tiers). Categories without a clean per-card price model
// — Damaged / Sealed Product / Needs Review — fall into Bulk. Bulk uses the
// lowest default per-card price ($0.05), so this conservative bucketing can
// only understate, never overstate, the revenue estimate. Users can refine
// the mix manually afterwards.
const SORTING_CATEGORY_TO_TIER: Record<SortingCategory, LotTier> = {
  "Common/Uncommon Bulk": "Bulk",
  "Trainers/Items/Supporters": "Bulk",
  Energy: "Bulk",
  Holos: "Holo",
  "Reverse Holos": "Holo",
  Promos: "Holo",
  Rares: "Rare",
  Hits: "Hits",
  Damaged: "Bulk",
  "Sealed Product": "Bulk",
  "Needs Review": "Bulk",
};

/**
 * Convert a saved PurchaseLot's sortingBins into a four-tier LotMix percentage
 * breakdown. Every category contributes to both the mix and `includedCount`,
 * so callers can use `includedCount` as the analyzer's card count and the
 * per-tier allocation will match exactly. Returns null when no bins have a
 * usable count so the caller can fall back to the lot's estimatedCards and
 * the analyzer's default mix.
 */
export function sortingBinsToMix(
  bins: SortingBin[],
): { mix: LotMix; includedCount: number } | null {
  const totals: Record<LotTier, number> = {
    Bulk: 0,
    Holo: 0,
    Rare: 0,
    Hits: 0,
  };
  let total = 0;
  for (const bin of bins) {
    // `bin.category` is now a game-defined string. This map only knows the
    // Pokémon taxonomy; unknown (non-Pokémon) categories return undefined and
    // are skipped, so a non-Pokémon lot's mix falls back to estimatedCards +
    // the default mix rather than mis-bucketing. Conservative by design.
    const tier = SORTING_CATEGORY_TO_TIER[bin.category as SortingCategory];
    if (!tier) continue;
    const count = Math.max(0, bin.count || 0);
    if (count <= 0) continue;
    totals[tier] += count;
    total += count;
  }
  if (total <= 0) return null;

  const pct: Record<LotTier, number> = {
    Bulk: (totals.Bulk / total) * 100,
    Holo: (totals.Holo / total) * 100,
    Rare: (totals.Rare / total) * 100,
    Hits: (totals.Hits / total) * 100,
  };
  // Largest-remainder rounding so percents are integers and sum to exactly 100.
  const floors: Record<LotTier, number> = {
    Bulk: Math.floor(pct.Bulk),
    Holo: Math.floor(pct.Holo),
    Rare: Math.floor(pct.Rare),
    Hits: Math.floor(pct.Hits),
  };
  let assigned = floors.Bulk + floors.Holo + floors.Rare + floors.Hits;
  let remainder = 100 - assigned;
  const order = (["Bulk", "Holo", "Rare", "Hits"] as LotTier[]).sort(
    (a, b) => pct[b] - floors[b] - (pct[a] - floors[a]),
  );
  for (const t of order) {
    if (remainder <= 0) break;
    floors[t] += 1;
    remainder -= 1;
  }
  return {
    mix: {
      bulk: floors.Bulk,
      holo: floors.Holo,
      rare: floors.Rare,
      hits: floors.Hits,
    },
    includedCount: total,
  };
}

/**
 * Auto-balance helper for the four-tier mix sliders. Adjusts the changed tier and
 * proportionally redistributes the delta across the others so the total stays at 100%.
 */
export function rebalanceMix(
  prev: LotMix,
  tier: LotTier,
  nextValue: number,
): LotMix {
  const target = clampMixPercent(nextValue);
  const others: LotTier[] = TIERS.filter((t) => t !== tier) as LotTier[];
  const otherKeys: Record<LotTier, keyof LotMix> = {
    Bulk: "bulk",
    Holo: "holo",
    Rare: "rare",
    Hits: "hits",
  };

  const next: LotMix = { ...prev, [otherKeys[tier]]: target };
  const remaining = 100 - target;
  const otherSum = others.reduce((s, t) => s + (prev[otherKeys[t]] || 0), 0);

  if (otherSum <= 0) {
    // Distribute remaining evenly among the other three.
    const each = remaining / 3;
    for (const t of others) next[otherKeys[t]] = each;
  } else {
    for (const t of others) {
      const share = (prev[otherKeys[t]] || 0) / otherSum;
      next[otherKeys[t]] = remaining * share;
    }
  }

  // Round to whole percents and fix any drift caused by rounding.
  const rounded: LotMix = {
    bulk: Math.round(next.bulk),
    holo: Math.round(next.holo),
    rare: Math.round(next.rare),
    hits: Math.round(next.hits),
  };
  const drift =
    100 - (rounded.bulk + rounded.holo + rounded.rare + rounded.hits);
  if (drift !== 0) {
    // Apply drift to the largest "other" bucket so the changed tier stays exact.
    const adjustables = others.map((t) => otherKeys[t]);
    let bestKey: keyof LotMix = adjustables[0];
    let bestVal = -1;
    for (const k of adjustables) {
      if (rounded[k] > bestVal) {
        bestVal = rounded[k];
        bestKey = k;
      }
    }
    rounded[bestKey] = Math.max(0, Math.min(100, rounded[bestKey] + drift));
  }
  return rounded;
}
